import { backgroundCheckpoint } from '../janus-engine/pl/background';

/** Let the caller finish and the browser render before optional cache encoding and compression. */
export async function yieldForCacheWrite(): Promise<void> {
    await backgroundCheckpoint();
    const scheduler = (globalThis as { scheduler?: { postTask(task: () => void, options: { priority: 'background' }): Promise<void> } }).scheduler;
    if (scheduler) { await scheduler.postTask(() => {}, { priority: 'background' }); return; }
    await new Promise<void>(resolve => {
        const channel = new MessageChannel();
        channel.port1.onmessage = () => { channel.port1.close(); channel.port2.close(); resolve(); };
        channel.port2.postMessage(null);
    });
}

/** Cache persistence is optional work, shared by both facets and postponed by every action. */
export function createIdleCacheWriter(delay = 2000, ready: () => Promise<unknown> = async () => {}) {
    const queued = new Map<string, () => Promise<void>>();
    let timer: ReturnType<typeof setTimeout> | undefined, actions = 0, generation = 0, draining = false;
    const arm = (): void => {
        clearTimeout(timer);
        if (!actions && queued.size) timer = setTimeout(() => { void drain(); }, delay);
    };
    async function drain(): Promise<void> {
        if (actions || draining) return;
        draining = true;
        const started = generation;
        try {
            await ready();
            await yieldForCacheWrite();
            for (const [key, task] of queued) {
                if (actions || generation !== started) break;
                queued.delete(key);
                try { await task(); } catch { /* A cache miss never invalidates the page's truth. */ }
            }
        } catch { /* Optional background scheduling cannot surface an action failure. */ }
        finally { draining = false; arm(); }
    }
    return {
        enqueue(key: string, task: () => Promise<void>): void { queued.set(key, task); arm(); },
        beginAction(): () => void {
            actions++; generation++; clearTimeout(timer);
            let finished = false;
            return () => { if (!finished) { finished = true; actions--; arm(); } };
        },
        /** Recheck after compression so a new click cancels an upload already preparing. */
        generation: () => generation,
    };
}
